import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Users } from 'lucide-react';
import { useState } from 'react';
import { Avatar, Badge, Button, EmptyState, Input, Select, Spinner, TrustBadge } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import type { AdminUserListItem, AdminUserListResponse } from '../features/users/queries';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';
import { imagePublicUrl } from '../lib/storage';

export const Route = createFileRoute('/_admin/users')({
  component: UsersScreen,
});

const COUNTRY_OPTIONS = [
  { value: 'all', label: '전체 국가' },
  { value: 'KR', label: '한국 (KR)' },
  { value: 'JP', label: '일본 (JP)' },
];

const SUSPENDED_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'true', label: '정지됨' },
  { value: 'false', label: '정상' },
];

const COUNTRY_LABEL: Record<string, string> = { KR: '한국', JP: '일본' };

function UsersScreen() {
  const [country, setCountry] = useState('all');
  const [suspended, setSuspended] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');

  const listQuery = useInfiniteQuery({
    queryKey: ['admin', 'users', 'list', { q, country, suspended }] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<AdminUserListResponse>('/v1/admin/users', {
        query: {
          q: q || undefined,
          country: country === 'all' ? undefined : country,
          suspended: suspended === 'all' ? undefined : suspended,
          cursor: pageParam ?? undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const items: AdminUserListItem[] = listQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader title="회원 관리" description="프로필 · 신뢰도 · 제재" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          className="w-36"
          aria-label="국가 필터"
          options={COUNTRY_OPTIONS}
          value={country}
          onValueChange={setCountry}
        />
        <Select
          className="w-32"
          aria-label="정지 여부 필터"
          options={SUSPENDED_OPTIONS}
          value={suspended}
          onValueChange={setSuspended}
        />
        <form
          className="w-72"
          onSubmit={(event) => {
            event.preventDefault();
            setQ(searchInput.trim());
          }}
        >
          <Input
            type="search"
            placeholder="닉네임·핸들 검색 (Enter)"
            aria-label="회원 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            clearable
            clearLabel="검색어 지우기"
          />
        </form>
      </div>

      {listQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="회원 목록 불러오는 중" />
        </div>
      ) : listQuery.isError ? (
        <EmptyState
          title="회원 목록을 불러오지 못했습니다"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : '잠시 후 다시 시도해 주세요'
          }
          action={
            <Button variant="tertiary" size="sm" onClick={() => void listQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Users size={40} strokeWidth={1.5} />}
          title="회원이 없습니다"
          description="조건에 맞는 회원이 없습니다"
        />
      ) : (
        <>
          <Table>
            <Thead>
              <tr>
                <Th>닉네임</Th>
                <Th className="w-20">국가</Th>
                <Th className="w-32">신뢰도</Th>
                <Th className="w-20">상태</Th>
                <Th className="w-36">가입일</Th>
              </tr>
            </Thead>
            <tbody>
              {items.map((user) => (
                <Tr key={user.id} className="hover:bg-subtle">
                  <Td>
                    <Link
                      to="/users/$id"
                      params={{ id: user.id }}
                      className="flex items-center gap-3"
                    >
                      <Avatar
                        size="sm"
                        name={user.display_name}
                        src={user.avatar_path != null ? imagePublicUrl(user.avatar_path) : undefined}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-fg hover:underline">
                          {user.display_name}
                        </span>
                        <span className="block truncate text-caption2 text-fg-tertiary">
                          @{user.handle}
                        </span>
                      </span>
                    </Link>
                  </Td>
                  <Td className="text-fg-secondary">{COUNTRY_LABEL[user.country] ?? user.country}</Td>
                  <Td>
                    <TrustBadge
                      altitude={user.trust_score}
                      size="sm"
                      meterAriaLabel={`${user.display_name} 신뢰도 ${user.trust_score}미터`}
                    />
                  </Td>
                  <Td>
                    {user.is_suspended ? (
                      <Badge tone="danger">정지</Badge>
                    ) : (
                      <Badge tone="success">정상</Badge>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap text-caption2 text-fg-tertiary">
                    {formatDateTime(user.created_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          {listQuery.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="tertiary"
                size="sm"
                loading={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                더 보기
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
